// app/javascript/quotes/components/App.jsx
import React from 'react'
import { QueryClientProvider } from '@tanstack/react-query';
import { createQueryClient, MutationErrorToaster } from 'utils/queryClientConfig';
import {
  BrowserRouter as Router,
} from 'react-router-dom'
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools'


import { DndProvider } from 'react-dnd'
import {HTML5Backend} from 'react-dnd-html5-backend';
import { ToastProvider } from 'ui/toast';

import AppBody from './AppBody'
import * as Sentry from '@sentry/react';
import ReactGA from 'react-ga';
import ErrorBoundary from './ErrorBoundary'
import SubscriberWrapper from './SubscriberWrapper'
import { OptimisticPromiseProvider } from 'contexts/OptimisticPromiseContext';
import { ActiveCodeProvider } from 'contexts/ActiveCodeContext';
import { CodePageModalProvider } from './contexts/CodePageModalContext';
import { ProjectModalProvider } from '../../contexts/ProjectModalContext';
import { AIChatProvider } from './contexts/AIChatContext';

ReactGA.initialize(GOOGLE_ANALYTICS_ID, {gaOptions: {
    allowLinker: true
}});


ReactGA.ga('require', 'linker');
ReactGA.ga('linker:autoLink', ['delvetool.com'] );

import 'sanitize.css'

// SENTRY_FRONTEND_DSN and SENTRY_ENVIRONMENT are injected at build time by
// DefinePlugin (see config/webpack/environment.js). Reference the globals
// directly — do NOT assign them to same-named consts, since DefinePlugin does a
// textual replace and would produce `const "staging" = ...`.
const sentryEnvironment = SENTRY_ENVIRONMENT || 'development';
// Enabled whenever a DSN is present. Production/staging set it via Heroku;
// locally, set SENTRY_FRONTEND_DSN to test (otherwise dev stays silent).
const sentryEnabled = Boolean(SENTRY_FRONTEND_DSN);
// Fraction of traced transactions that also get CPU-profiled. Set per
// environment with the SENTRY_PROFILING_SAMPLE_RATE Heroku config var — e.g.
// 1.0 on staging (to experience full profiling) and 0.2 in prod (enough data
// without profiling every transaction). 0 turns profiling off, so it doubles as
// the kill switch. Baked in at build time, so changes need a rebuild + redeploy.
// Defaults to 0.2 when unset or invalid; set it in .env for full local profiling.
const parsedProfilesSampleRate = parseFloat(SENTRY_PROFILING_SAMPLE_RATE);
const profilesSampleRate = Number.isFinite(parsedProfilesSampleRate)
  ? Math.min(Math.max(parsedProfilesSampleRate, 0), 1)
  : 0.2;

Sentry.init({
  dsn: SENTRY_FRONTEND_DSN,
  environment: sentryEnvironment,
  enabled: sentryEnabled,
  sendDefaultPii: false,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
  ],
  // 100% tracing — at current traffic this stays within Sentry's included 5M
  // spans/mo; revisit if usage approaches the cap.
  tracesSampleRate: 1.0,
  // Profiling is relative to tracesSampleRate: only sampled transactions get a
  // profile. Requires the `Document-Policy: js-profiling` response header (set
  // in ApplicationController) and a Chromium browser — the JS Self-Profiling
  // API is Chromium-only, so profiles won't appear in Firefox/Safari.
  // Set via SENTRY_PROFILING_SAMPLE_RATE (see computation above).
  profilesSampleRate,
  tracePropagationTargets: ['localhost', /^\//, /^https:\/\/(www\.|staging\.)?delvetool\.com\/api/],
  enableLogs: true,
});

// Create a client
const queryClient = createQueryClient();

class App extends React.Component {
  constructor() {
    super();
    this.state = {}
  }

  render ()
  {
    return (
      <div>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
              {/* <ReactQueryDevtools initialIsOpen={false} /> */}

              <DndProvider backend={HTML5Backend}>
                <ToastProvider>
                  <MutationErrorToaster queryClient={queryClient} />
                  <ActiveCodeProvider>
                    <OptimisticPromiseProvider>
                      <AIChatProvider>
                        <ProjectModalProvider>
                          <Router>
                            <CodePageModalProvider>  
                              <SubscriberWrapper>
                                <AppBody/>
                              </SubscriberWrapper>
                            </CodePageModalProvider>
                          </Router>
                        </ProjectModalProvider>
                      </AIChatProvider>
                    </OptimisticPromiseProvider>
                  </ActiveCodeProvider>
                </ToastProvider>
              </DndProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </div>
    );
  }
}

export default App
